import { uploadWorkspaceFile } from '../api/workspace';

/**
 * 빈 문서 만들기 (2026-08-14) — `＋ 만들기 또는 업로드` 의 문서 5종.
 *
 * 지금까지 워크스페이스에서 만들 수 있는 것은 **폴더뿐**이었다. 문서는 어딘가에서 만들어
 * 올려야 했고, 인앱 편집기는 이미 있는 파일 위에만 떴다. 여기서 씨앗 파일을 만들어
 * 그 편집기로 곧장 넘긴다.
 *
 * 서버에 새 엔드포인트를 두지 않는다 — 빈 문서를 만드는 데 필요한 것은 "정해진 바이트열"
 * 뿐이라, 기존 presign → PUT → finalize 경로를 그대로 타면 쿼터·MIME 화이트리스트·
 * 매직바이트 검증이 자동으로 같은 규칙으로 적용된다. 라우트를 한 벌 더 만들 이유가 없다.
 *
 * ⚠ docx/xlsx/pptx 씨앗은 `public/templates/blank.*` 에 커밋돼 있다
 *   (`pnpm gen:blank-templates` 로 재생성). 손으로 최소 OOXML 을 짜면 편집기가 기대하는
 *   스타일·numbering 정의가 빠져 "새 문서인데 목록이 안 먹는다" 가 된다.
 */

// 2026-08-14 (2차) — 한글(.hwpx) 추가. 사용자 요구: "만들기 또는 업로드에 hwpx 도 추가해줘".
//   .hwp(CFB) 가 아니라 **.hwpx** 다 — 교체 저장이 MIME 을 못 바꾸므로 새 문서의 형식이
//   곧 그 파일의 평생 형식이 되고, hwpx 가 열기·저장 모두 안정적이다(hwp-save-ops 참조).
export const NEW_DOC_KINDS = ['docx', 'xlsx', 'pptx', 'hwpx', 'md', 'txt'] as const;
export type NewDocKind = (typeof NEW_DOC_KINDS)[number];

interface KindSpec {
  ext: string;
  mimeType: string;
  /** 템플릿 파일 경로. 없으면 `inline` 바이트로 만든다. */
  template?: string;
  /** 텍스트 계열의 초기 내용. */
  inline?: string;
}

/**
 * ⚠ `sizeBytes` 스키마 최소값이 1 이라 **0바이트로 만들 수 없다.** 빈 .txt 를 정말 비워
 *   보내면 presign 이 400 으로 떨어진다. 줄바꿈 한 글자를 넣어 그 경계를 피한다.
 */
const SPEC: Readonly<Record<NewDocKind, KindSpec>> = {
  docx: {
    ext: '.docx',
    mimeType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    template: '/templates/blank.docx',
  },
  xlsx: {
    ext: '.xlsx',
    mimeType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    template: '/templates/blank.xlsx',
  },
  pptx: {
    ext: '.pptx',
    mimeType: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    template: '/templates/blank.pptx',
  },
  /*
   * 한글 (2026-08-14 2차). 씨앗은 **한글 엔진이 직접 만든 빈 문서**다
   * (gen 스크립트가 @rhwp/core 의 createBlankDocument → exportHwpx 를 돌린다).
   * ⚠ MIME 은 `application/hwp+zip` — 서버 허용 목록과 매직바이트 표(PK) 양쪽에 있는 값이라야
   *   finalize 가 통과한다(packages/office/src/hwp-save-ops.ts 의 HWP_MIME 과 같은 값).
   */
  hwpx: {
    ext: '.hwpx',
    mimeType: 'application/hwp+zip',
    template: '/templates/blank.hwpx',
  },
  md: { ext: '.md', mimeType: 'text/markdown', inline: '\n' },
  txt: { ext: '.txt', mimeType: 'text/plain', inline: '\n' },
};

/** 이 유형의 확장자 (파일명 기본값 조립용). */
export function extensionOf(kind: NewDocKind): string {
  return SPEC[kind].ext;
}

/** 사용자가 확장자를 빼먹었으면 붙인다. 이미 있으면 그대로 둔다(중복 방지). */
export function withExtension(name: string, kind: NewDocKind): string {
  const ext = SPEC[kind].ext;
  return name.toLowerCase().endsWith(ext) ? name : `${name}${ext}`;
}

/**
 * 빈 문서를 만들어 워크스페이스에 올린다. 성공하면 새 fileId.
 *
 * 템플릿 fetch 는 같은 출처의 정적 자산이라 인증이 필요 없다. 실패하면(배포 누락 등)
 * 그대로 던져서 호출부가 사용자에게 사유를 보여 준다 — 조용히 빈 파일을 만들지 않는다.
 */
export async function createBlankDocument(
  kind: NewDocKind,
  name: string,
  parentId: string | null,
): Promise<{ fileId: string; name: string }> {
  const spec = SPEC[kind];
  const filename = withExtension(name.trim(), kind);

  let blob: Blob;
  if (spec.template) {
    const res = await fetch(spec.template);
    if (!res.ok) {
      throw new Error(`템플릿을 불러오지 못했습니다 (${spec.template}, HTTP ${res.status})`);
    }
    blob = await res.blob();
  } else {
    blob = new Blob([spec.inline ?? '\n'], { type: spec.mimeType });
  }

  // ⚠ File 의 type 을 반드시 지정한다. 비워 두면 uploadWorkspaceFile 이
  //   application/octet-stream 으로 선언하고, 서버가 그 MIME 으로 저장해 편집기가
  //   형식을 못 알아본다(목록 아이콘도 '기타'가 된다).
  const file = new File([blob], filename, { type: spec.mimeType });
  const { fileId } = await uploadWorkspaceFile(file, parentId);
  return { fileId, name: filename };
}
